/**
 * 판정 기록 목록 페이지 — URL ↔ 필터 동기화·사전 검증·요약·목록·페이징(D-12).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 비즈니스 로직 전담(ARCHITECTURE §1): URL에서 필터를 파싱하고, 기간·product를 사전 검증해
 * 위반이면 요청을 막고(enabled=false) 입력 옆 문구를 보인다. 조회는 훅에 위임, 표시는 organism에 위임.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "components/atoms/Button";
import { ToastManager } from "components/organisms/CommonUI";
import DoseReviewLogListSection from "components/organisms/DoseReviewLogListSection";
import DoseReviewLogSummary from "components/organisms/DoseReviewLogSummary";
import { DoseApiError } from "services/api/doseApiError";
import { doseReviewLogsApi } from "services/api/doseReviewLogs/doseReviewLogsApi";
import DownloadUtil from "utils/DownloadUtil";
import {
  useDoseReviewLogs,
  useDoseReviewLogSummary,
} from "hooks/doseReviewLogs/useDoseReviewLogs";
import {
  parseFilters,
  filtersToSearch,
  toDetailSearch,
  toApiFilter,
  validatePeriod,
  validateProduct,
  hasNonPeriodFilter,
  defaultFilterState,
  csvFilename,
  PERIOD_INVALID_MESSAGE,
  INPUT_ERROR_TEXT,
  CSV_TOO_LARGE_TOAST,
  CSV_ABORTED_TOAST,
  CSV_ERROR_TOAST,
  CSV_EXPORT_LABEL,
  type DoseReviewLogFilterState,
} from "utils/doseReviewLogs";
import type { DoseReviewLogListItem } from "services/api/doseReviewLogs/types";
import FilterBar from "./FilterBar";
import { PageWrap, PageTitle, Toolbar, Pager } from "./filterStyle";

const LIMIT = 50;

const isHttp400 = (err: unknown): boolean =>
  !!err && typeof err === "object" && (err as { response?: { status?: number } }).response?.status === 400;

const DoseReviewLogsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = parseFilters(searchParams);

  // 첫 진입(URL에 기간 없음) — 기본 기간을 URL에 replace로 고정(이미 훅은 기본 기간으로 요청).
  useEffect(() => {
    if (!searchParams.get("start") || !searchParams.get("end")) {
      setSearchParams(filtersToSearch(filters), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodCheck = validatePeriod(filters.start, filters.end);
  const productCheck = validateProduct(filters.product);
  const enabled = periodCheck.ok && productCheck.ok;

  const apiFilter = toApiFilter(filters);
  const logs = useDoseReviewLogs({ ...apiFilter, limit: LIMIT, offset: filters.offset }, enabled);
  const summary = useDoseReviewLogSummary(apiFilter, enabled);

  const commit = (patch: Partial<DoseReviewLogFilterState>) => {
    const next = { ...filters, ...patch };
    if (!("offset" in patch)) next.offset = 0;
    setSearchParams(filtersToSearch(next));
  };

  // 오류 매핑 — 6125는 기간 옆 문구(사전 검증이 먼저 막으므로 방어), HTTP 400은 "입력값 오류".
  let periodMessage = periodCheck.ok ? undefined : periodCheck.message;
  const productMessage = productCheck.ok ? undefined : productCheck.message;
  let listIsError = logs.isError;
  let listErrorText: string | undefined;
  if (logs.isError) {
    const err = logs.error;
    if (err instanceof DoseApiError && err.code === 6125) {
      periodMessage = PERIOD_INVALID_MESSAGE;
      listIsError = false;
    } else if (isHttp400(err)) {
      listErrorText = INPUT_ERROR_TEXT;
    }
  }

  const items: DoseReviewLogListItem[] = logs.data?.items ?? [];
  const total = logs.data?.total ?? 0;
  const emptyVariant = hasNonPeriodFilter(filters) ? "no-match" : "no-records";
  const hasPrev = filters.offset > 0;
  const hasNext = filters.offset + LIMIT < total;

  const refreshAll = () => {
    logs.refetch();
    summary.refetch();
  };

  const resetFilters = () => setSearchParams(filtersToSearch(defaultFilterState()));

  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    if (exporting || !enabled) return;
    setExporting(true);
    try {
      const result = await doseReviewLogsApi.exportCsv(apiFilter);
      if (result.kind === "error") {
        ToastManager.show({
          content: result.envelope.error === 6124 ? CSV_TOO_LARGE_TOAST : CSV_ERROR_TOAST,
        });
        return;
      }
      DownloadUtil.downloadBlob(result.blob, csvFilename(filters.start, filters.end));
    } catch (err) {
      // 네트워크 오류·타임아웃(응답 없음)은 "중단", HTTP 오류(응답 있음)는 일반 실패(계획 §6.2 14단계).
      const hasResponse = !!(err && typeof err === "object" && (err as { response?: unknown }).response);
      ToastManager.show({ content: hasResponse ? CSV_ERROR_TOAST : CSV_ABORTED_TOAST });
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageWrap data-testid="dose-review-logs-page">
      <PageTitle>판정 기록</PageTitle>

      <FilterBar
        filters={filters}
        periodMessage={periodMessage}
        productMessage={productMessage}
        onCommit={commit}
      />

      <Toolbar>
        <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={refreshAll} data-testid="dose-log-refresh">
          새로고침
        </Button>
        <Button buttonType="button" type="Text" size="Small" variant="Ghost" color="Secondary" onClick={resetFilters} data-testid="dose-log-reset">
          필터 초기화
        </Button>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Outlined"
          color="Primary"
          disabled={exporting || !enabled}
          onClick={onExport}
          data-testid="dose-log-export"
        >
          {CSV_EXPORT_LABEL}
        </Button>
      </Toolbar>

      {/* 사전 검증 위반(enabled=false)이면 요청을 보내지 않으므로 요약·목록을 그리지 않는다
          — FilterBar의 기간/product 옆 문구가 상태를 알린다(요약 영구 스피너·오해 빈 상태 방지). */}
      {enabled && (
        <>
          <DoseReviewLogSummary
            isLoading={summary.isLoading}
            isError={summary.isError}
            onRetry={() => summary.refetch()}
            data={summary.data}
            onSelectRule={(ruleId) => commit({ ruleId })}
          />

          <DoseReviewLogListSection
            isLoading={logs.isLoading}
            isError={listIsError}
            errorText={listErrorText}
            onRetry={() => logs.refetch()}
            items={items}
            total={total}
            emptyVariant={emptyVariant}
            onResetFilter={resetFilters}
            rowHref={(item) => `/admin/dose-review-logs/${item.id}?${toDetailSearch(filters)}`}
            onRowClick={(item) =>
              navigate(`/admin/dose-review-logs/${item.id}?${toDetailSearch(filters)}`)
            }
          />
        </>
      )}

      {enabled && (hasPrev || hasNext) && (
        <Pager>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            disabled={!hasPrev}
            onClick={() => commit({ offset: Math.max(0, filters.offset - LIMIT) })}
            data-testid="dose-log-prev"
          >
            이전
          </Button>
          <span>
            {Math.floor(filters.offset / LIMIT) + 1} 페이지
          </span>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            disabled={!hasNext}
            onClick={() => commit({ offset: filters.offset + LIMIT })}
            data-testid="dose-log-next"
          >
            다음
          </Button>
        </Pager>
      )}
    </PageWrap>
  );
};

export default DoseReviewLogsPage;
